/* Player Performance Index (PPI) — build 221.
   ─────────────────────────────────────────────────────────────────
   PPI = Scoring Index + Possession Index   (rounded to 1 decimal)

   Scoring Index
     = (player PTS / team PTS)
     + efficiency ratio

   Efficiency ratio
     • If player has FTA: (player eFG% + player FT%) / (team eFG% + team FT%)
     • If player has no FTA: player eFG% / team eFG%
     where eFG% = (FGM + 0.5 × 3PM) / FGA

   Possession Index
     = (player REB / team REB + player STL / team STL)
     − (player TO / team TO)

   All values are percentages. Div-by-zero contributes 0.

   Implementation notes
   • Every percentage is computed as `(num / denom) * 100` so the
     result is in percentage-point space (0..100ish, not 0..1). The
     PTS-share term, the REB / STL / TO terms, and the eFG% and FT%
     inputs to the efficiency ratio all live on this scale.
   • The efficiency ratio is itself a ratio of percentages, so the
     units cancel — we keep it on the same percentage-point scale by
     leaving it un-rescaled (a "1.0" ratio reads as "1.0" not "100").
     If you need it to land on a different scale to match an external
     formula, edit `EFF_RATIO_SCALE` below.
   • Team-totals row receives `null` from `computePPI` because every
     player/team ratio collapses to 1.0 there — the UI shows '—'. */

const EFF_RATIO_SCALE = 1; // bump to 100 if the spec means
                           // "ratio expressed as percent (×100)"

// Derive points from raw makes (canonical in this app — never trust
// a stored `pts` field). 2-pt make = (fgm - 3pm)*2, 3-pt make = 3pm*3,
// FT = ftm.
function derivePts(row) {
  const fgm = +row?.fgm || 0;
  const tpm = +row?.three_pm || 0;
  const ftm = +row?.ftm || 0;
  return Math.max(0, fgm - tpm) * 2 + tpm * 3 + ftm;
}

// Safe percentage. Zero denom → 0 so a missing team stat doesn't
// poison the index with Infinity / NaN.
function pct(num, denom) {
  const d = +denom || 0;
  if (d === 0) return 0;
  return ((+num || 0) / d) * 100;
}

// eFG% as a percentage (0..100). Zero FGA → 0.
function efgPct(row) {
  const fga = +row?.fga || 0;
  if (fga === 0) return 0;
  const fgm = +row?.fgm || 0;
  const tpm = +row?.three_pm || 0;
  return ((fgm + 0.5 * tpm) / fga) * 100;
}

// FT% as a percentage (0..100). Zero FTA → 0.
function ftPct(row) {
  const fta = +row?.fta || 0;
  if (fta === 0) return 0;
  return ((+row?.ftm || 0) / fta) * 100;
}

/* Compute PPI for a single player against their team's totals.
   Returns a number rounded to 1 decimal, or null when:
     • `playerRow` is null/undefined
     • `playerRow` IS the team totals row (ratios collapse to 1.0). */
export function computePPI(playerRow, teamTotals) {
  if (!playerRow || !teamTotals) return null;
  if (playerRow.is_team_total) return null;
  if (playerRow.box_score_id && teamTotals.box_score_id === playerRow.box_score_id) return null;

  // PTS share (percentage)
  const pPts = derivePts(playerRow);
  const tPts = derivePts(teamTotals);
  const ptsShare = pct(pPts, tPts);

  // Efficiency ratio
  const pEfg = efgPct(playerRow);
  const tEfg = efgPct(teamTotals);
  const pFt = ftPct(playerRow);
  const tFt = ftPct(teamTotals);
  const playerHasFTA = (+playerRow.fta || 0) > 0;

  let effRatio = 0;
  if (playerHasFTA) {
    const denom = tEfg + tFt;
    if (denom > 0) effRatio = ((pEfg + pFt) / denom) * EFF_RATIO_SCALE;
  } else {
    if (tEfg > 0) effRatio = (pEfg / tEfg) * EFF_RATIO_SCALE;
  }

  const scoring = ptsShare + effRatio;

  // Possession Index
  const pReb = (+playerRow.oreb || 0) + (+playerRow.dreb || 0);
  const tReb = (+teamTotals.oreb || 0) + (+teamTotals.dreb || 0);
  const possession =
    pct(pReb, tReb) +
    pct(playerRow.stl, teamTotals.stl) -
    pct(playerRow.to, teamTotals.to);

  return Math.round((scoring + possession) * 10) / 10;
}

/* Format a PPI value for display.
   • Null → '—' (used for team totals row).
   • Otherwise the raw number with exactly one decimal place. */
export function formatPPI(ppi) {
  if (ppi === null || ppi === undefined || Number.isNaN(ppi)) return '—';
  return ppi.toFixed(1);
}

/* Average a list of player rows from multiple games into a single
   synthetic row. Used by the Coach public view to compute PPI from
   averaged raw counting stats across selected games (spec: "averages
   raw stats across all selected games first, then recalculates PPI
   from those averaged totals"). */
export function averageRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const COUNTING = ['fgm', 'fga', 'three_pm', 'three_pa', 'ftm', 'fta', 'oreb', 'dreb', 'ast', 'stl', 'blk', 'to', 'pf'];
  const n = rows.length;
  const sum = {};
  COUNTING.forEach(k => { sum[k] = 0; });
  rows.forEach(r => {
    COUNTING.forEach(k => { sum[k] += (+r?.[k] || 0); });
  });
  const avg = {};
  COUNTING.forEach(k => { avg[k] = Math.round((sum[k] / n) * 10) / 10; });
  avg.reb = Math.round((avg.oreb + avg.dreb) * 10) / 10;
  const base = rows[0] || {};
  return {
    ...base,
    ...avg,
    pts: derivePts(avg),
  };
}
